import {
  concat,
  formatUnits,
  numberToHex,
  parseUnits,
  size,
  type Address,
  type Hex,
  type TypedDataDefinition,
} from 'viem';

import { truncateAmountToDecimals } from '@/app/shared/lib/formatters';

export type NormalizedTx = {
  to: string;
  data: string;
  value: string;
  gas?: number | string;
  gasPrice?: number | string;
};

export type NormalizedQuote = {
  protocol: string;
  amountReturned: string;
  tokenApprovalAddress?: string;
  logo: string;
  tx: NormalizedTx | null;
  estimatedGas?: number;
  isSignatureNeededForSwap: boolean;
  permit2Eip712?: TypedDataDefinition;
};

export function normalizeTx(rawQuote: Record<string, unknown>): NormalizedTx | null {
  try {
    const tx = rawQuote.transaction as NormalizedTx | undefined;
    if (!tx?.to || !tx?.data) return null;
    return { to: tx.to, data: tx.data, value: tx.value, gas: tx.gas, gasPrice: tx.gasPrice };
  } catch (err) {
    console.error('normalizeTx error:', err);
    return null;
  }
}

export function normalizePermit2Eip712(
  rawQuote: Record<string, unknown>,
): TypedDataDefinition | undefined {
  const permit2 = rawQuote.permit2 as { eip712?: TypedDataDefinition } | undefined;
  const eip712 = permit2?.eip712;
  if (!eip712?.domain || !eip712?.types || !eip712?.message || !eip712?.primaryType) {
    return undefined;
  }
  const types = { ...(eip712.types as Record<string, unknown>) };
  delete types.EIP712Domain;
  return {
    domain: eip712.domain,
    types: types as TypedDataDefinition['types'],
    primaryType: eip712.primaryType,
    message: eip712.message,
  };
}

export function appendPermit2Signature(data: Hex, signature: Hex): Hex {
  return concat([data, numberToHex(size(signature), { signed: false, size: 32 }), signature]);
}

export function shouldSignPermit2(quote: {
  isSignatureNeededForSwap?: boolean;
  permit2Eip712?: TypedDataDefinition;
}): quote is { permit2Eip712: TypedDataDefinition } {
  return Boolean(quote.permit2Eip712);
}

export function toRawAmount(amount: string, decimals: number): string {
  try {
    if (!amount || amount === '0') return '';
    return parseUnits(truncateAmountToDecimals(amount, decimals), decimals).toString();
  } catch {
    return '';
  }
}

export function toRawAmountBigInt(amount: string, decimals: number): bigint {
  try {
    if (!amount) return 0n;
    return parseUnits(truncateAmountToDecimals(amount, decimals), decimals);
  } catch {
    return 0n;
  }
}

export function fromRawAmount(raw: string, decimals: number): string {
  try {
    return formatUnits(BigInt(raw), decimals);
  } catch {
    return '';
  }
}

export function calcRate(sellAmount: string, buyAmount: string): string | null {
  const s = Number(sellAmount);
  const b = Number(buyAmount);
  if (!s || !b) return null;
  return (b / s).toFixed(6);
}

export function calcPriceImpact(
  sellAmount: string,
  sellPrice: number,
  buyAmount: string,
  buyPrice: number,
): string | null {
  const sellUsd = Number(sellAmount) * sellPrice;
  const buyUsd = Number(buyAmount) * buyPrice;
  if (!sellUsd || !buyUsd) return null;
  const impactPct = ((sellUsd - buyUsd) / sellUsd) * 100;
  return Math.max(0, impactPct).toFixed(2);
}

export type SwapSendTx = {
  to: Address;
  data: Hex;
  value: bigint;
  gas?: bigint;
  gasPrice?: bigint;
};

type SignTypedDataFn = (args: {
  domain: TypedDataDefinition['domain'];
  types: TypedDataDefinition['types'];
  primaryType: string;
  message: Record<string, unknown>;
}) => Promise<Hex>;

export async function buildSignedSwapSendTx(
  quote: NormalizedQuote,
  signTypedData: SignTypedDataFn,
): Promise<SwapSendTx> {
  if (!quote.tx?.to || !quote.tx.data) {
    throw new Error('Swap transaction is not available for this quote');
  }

  const tx = quote.tx;
  let data = tx.data as Hex;

  if (shouldSignPermit2(quote)) {
    const signature = await signTypedData({
      domain: quote.permit2Eip712.domain,
      types: quote.permit2Eip712.types,
      primaryType: quote.permit2Eip712.primaryType,
      message: quote.permit2Eip712.message as Record<string, unknown>,
    });
    data = appendPermit2Signature(data, signature);
  } else if (quote.isSignatureNeededForSwap) {
    throw new Error('Permit2 EIP-712 payload is missing from the swap quote');
  }

  const gasLimit =
    tx.gas && Number(tx.gas) > 0
      ? BigInt(tx.gas)
      : quote.estimatedGas
        ? BigInt(Math.ceil(quote.estimatedGas * 1.2))
        : undefined;

  return {
    to: tx.to as Address,
    data,
    value: BigInt(tx.value ?? '0'),
    ...(gasLimit ? { gas: gasLimit } : {}),
    ...(tx.gasPrice ? { gasPrice: BigInt(tx.gasPrice) } : {}),
  };
}
